import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
  TablePagination,
  Skeleton,
} from '@mui/material';
import {
  Add,
  Edit,
  Search,
  Phone,
  Email,
  Business,
  Refresh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import crmService, { Lead } from '../services/crmService';
import { useAuth } from '../contexts/AuthContext';

const LeadsPage: React.FC = () => {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  const [formData, setFormData] = useState<Partial<Lead>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    title: '',
    status: 'New',
    rating: 'Warm',
    description: '',
  });

  // Fetch leads
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await crmService.getLeads();
      // Check if response is an array or has a data/leads property
      const leadsData = Array.isArray(response) ? response : 
                       response.leads ? response.leads : 
                       response.data ? response.data : [];
      setLeads(leadsData);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setSnackbar({ open: true, message: 'Failed to fetch leads', severity: 'error' });
      setLeads([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle dialog open/close
  const handleOpenDialog = (lead?: Lead) => {
    if (lead) {
      setEditingLead(lead);
      setFormData(lead);
    } else {
      setEditingLead(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        title: '',
        status: 'New',
        rating: 'Warm',
        description: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLead(null);
  };

  // Handle save (create/update)
  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.company) {
        setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
        return;
      }

      if (!formData.description || formData.description.trim() === '') {
        setSnackbar({ open: true, message: 'Description is required', severity: 'error' });
        return;
      }

      // Clean up data - remove fields we don't want to send
      const cleanData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        title: formData.title,
        status: formData.status,
        rating: formData.rating,
        description: formData.description,
        leadSource: 'Partner Referral',
      };

      // Remove empty string values (but keep undefined for optional fields)
      Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === '') {
          delete cleanData[key];
        }
      });

      if (editingLead) {
        await crmService.updateLead(editingLead._id!, cleanData);
        setSnackbar({ open: true, message: 'Lead updated successfully', severity: 'success' });
      } else {
        // Auto-assign to current user when creating new lead
        const leadData = {
          ...cleanData,
          assignedTo: user?.id
        };
        await crmService.createLead(leadData as Omit<Lead, '_id'>);
        setSnackbar({ open: true, message: 'Lead created successfully', severity: 'success' });
      }
      handleCloseDialog();
      fetchLeads();
    } catch (error) {
      console.error('Error saving lead:', error);
      setSnackbar({ open: true, message: 'Failed to save lead', severity: 'error' });
    }
  };


  // Filter leads based on search
  const filteredLeads = leads.filter(lead =>
    `${lead.firstName} ${lead.lastName} ${lead.company} ${lead.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
      'New': 'info',
      'Contacted': 'primary',
      'Qualified': 'success',
      'Unqualified': 'warning',
      'Converted': 'success',
      'Lost': 'error'
    };
    return colors[status] || 'default';
  };

  // Get rating icon
  const getRatingIcon = (rating?: string) => {
    const icons = {
      'Hot': '🔥',
      'Warm': '☀️',
      'Cold': '❄️'
    };
    return rating ? icons[rating as keyof typeof icons] || '' : '';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight="bold">
            Leads Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchLeads}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              New Lead
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search leads by name, company, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Leads Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={7}>
                        <Skeleton variant="text" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        No leads found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((lead) => (
                      <TableRow key={lead._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {lead.firstName} {lead.lastName}
                            </Typography>
                            {lead.title && (
                              <Typography variant="caption" color="text.secondary">
                                {lead.title}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" color="action" />
                            {lead.company}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email fontSize="small" color="action" />
                            {lead.email}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {lead.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Phone fontSize="small" color="action" />
                              {lead.phone}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lead.status}
                            color={getStatusColor(lead.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {lead.rating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>{getRatingIcon(lead.rating)}</span>
                              {lead.rating}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(lead)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredLeads.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingLead ? 'Edit Lead' : 'Create New Lead'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title *"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  error={!formData.title}
                  placeholder="e.g., CTO, Sales Manager, IT Director"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name *"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  error={!formData.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name *"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  error={!formData.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!formData.email}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company *"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  error={!formData.company}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  disabled
                  helperText="Status is managed by the system"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Rating"
                  name="rating"
                  value={formData.rating}
                  onChange={handleChange}
                >
                  <MenuItem value="Hot">🔥 Hot</MenuItem>
                  <MenuItem value="Warm">☀️ Warm</MenuItem>
                  <MenuItem value="Cold">❄️ Cold</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  helperText="Please provide details about this lead (required)"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #63739c 0%, #aeb6bf 100%)',
              }}
            >
              {editingLead ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </motion.div>
    </Container>
  );
};

export default LeadsPage;